import { AppBar, Box, Typography } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";
import { getEnvironmentLabel } from "../../common/helpers/envHelpers";
import { getInitials } from "../../common/helpers/getInitials";
import { User } from "../user/user";
import { DRAWER_WIDTH } from "./HomeDrawer";
import { StyledToolbar } from "./styles";
import ToolbarMenu from "./toolbarMenu/ToolbarMenu";
import ToolbarMenuButton from "./toolbarMenu/ToolbarMenuButton";
import { AnnouncementBanner } from "../settings/announcements/AnnouncementBanner";
import { useAppSelector } from "../../app/hooks";
import { APP_BAR_HEIGHT, HOME_HEADER_HEIGHT_VAR } from "../../app/common";
import { selectActiveAnnouncements } from "../settings/announcements/announcementSlice";
import { AnnouncementItem } from "../settings/announcements/types";
import { selectExpertUserById } from "../user/expertUsersSlice";
import { isOutOfOfficeActive } from "./toolbarMenu/statusMessageMappers";

interface HomeMenuProps {
  user: User;
  fullWidth?: boolean;
}

export const HomeHeaderSpacer = () => (
  <Box
    aria-hidden
    sx={{
      minHeight: `var(${HOME_HEADER_HEIGHT_VAR}, ${APP_BAR_HEIGHT}px)`,
    }}
  />
);

export const HomeMenu = ({ user, fullWidth = false }: HomeMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const appBarRef = useRef<HTMLDivElement>(null);

  const handleOpenToolbarMenu = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);

  const handleCloseToolbarMenu = () => setAnchorEl(null);

  const fullName = `${user.firstName} ${user.lastName}`;
  const firstName = user.firstName || "Matti";
  const initials = getInitials(fullName) || "MM";
  const environmentLabel = getEnvironmentLabel();

  const announcements: AnnouncementItem[] = useAppSelector((state) =>
    selectActiveAnnouncements(state),
  );
  const expert = useAppSelector((state) =>
    selectExpertUserById(state, user.id),
  );
  const isAway = isOutOfOfficeActive(expert?.account.outOfOffice);

  useLayoutEffect(() => {
    const el = appBarRef.current;
    if (!el) return;

    const syncHeight = () => {
      document.documentElement.style.setProperty(
        HOME_HEADER_HEIGHT_VAR,
        `${el.offsetHeight}px`,
      );
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(HOME_HEADER_HEIGHT_VAR);
    };
  }, []);

  return (
    <AppBar
      ref={appBarRef}
      sx={{
        width: fullWidth ? "100%" : `calc(100% - ${DRAWER_WIDTH}px)`,
        borderBottom: "1px #F7F7F7 solid",
      }}
      position="fixed"
    >
      {announcements.map((announcement) => (
        <AnnouncementBanner key={announcement.id} announcement={announcement} />
      ))}
      <StyledToolbar disableGutters>
        {environmentLabel && (
          <Box
            sx={{
              position: "absolute",
              left: `calc(50vw - ${DRAWER_WIDTH}px)`,
              transform: "translateX(-50%)",
              pointerEvents: "none",
            }}
          >
            <Typography
              fontWeight={500}
              fontSize={28}
              textTransform="uppercase"
              letterSpacing={1.2}
              color="error.light"
            >
              {environmentLabel}
            </Typography>
          </Box>
        )}
        <ToolbarMenuButton
          onClick={handleOpenToolbarMenu}
          name={firstName}
          initials={initials}
          isAway={isAway}
        />
        <ToolbarMenu
          onClose={handleCloseToolbarMenu}
          anchorEl={anchorEl}
          fullName={fullName}
          username={user.email}
        />
      </StyledToolbar>
    </AppBar>
  );
};
